import { h, Fragment } from 'preact';
import LocatorIcon from 'utils/icon/icon-locator.svg';
import { useEffect, useMemo, useState } from 'preact/hooks';
import ChevronRightIcon from 'utils/icon/icon-chevron-right.svg';
import clsx from 'clsx';
import BookmarkIcon from 'utils/icon/icon-bookmark.svg';
import MapPinIcon from 'utils/icon/icon-map-pin.svg';
import { getCookie, getStoreStatus } from 'utils/helpers';
import { useLocations } from '../store-locator/locations-context';

const ProductStore = ({ currentVariant }) => {
  const {
    userCurrentLocation,
    useMyLocation,
    results,
    stockData,
    setUserSavedStoreId,
    userSavedStoreId,
    userSavedStore,
    getLocationsByDistance,
  } = useLocations();

  const [additionalStores, setStores] = useState(null);

  const stores = useMemo(() => {
    if (userSavedStore) {
      return [
        results.find((item) => item.id === userSavedStore.id) || userSavedStore,
        ...results.filter((item) => item.id !== userSavedStore.id).slice(0, 2),
        ...(additionalStores || []),
      ].slice(0, 3);
    }
    return results.slice(0, 3);
  }, [userSavedStore, results, additionalStores]);

  useEffect(() => {
    if (userSavedStore && stores.length < 3 && additionalStores === null) {
      const lat = userSavedStore.address?.latitude || userSavedStore.lat?.value;
      const lng =
        userSavedStore.address?.longitude || userSavedStore.lng?.value;
      if (lat && lng) {
        setStores(
          getLocationsByDistance({
            lat: parseFloat(lat),
            lng: parseFloat(lng),
          })
            ?.filter((location) => location.storeId !== userSavedStore.storeId)
            .slice(0, 2)
            .map((location) => {
              const { metersAway, ...item } = location;
              return item;
            }) || [],
        );
      }
    }
  }, [stores, userSavedStore, additionalStores]);

  useEffect(() => {
    if (
      navigator.geolocation &&
      !getCookie('geo_disabled') &&
      !getCookie('geo_enabled')
    ) {
      useMyLocation();
    }
  }, []);

  return (
    <div className="py-16 lc:lg:py-20" id="find-in-store">
      <div
        className={clsx('py-16 ml:py-0 ml:text-center', {
          'ml:!py-24':
            !currentVariant?.id || (!userCurrentLocation && !userSavedStore),
        })}
      >
        <div className="flex gap-8 items-center mb-8 ml:mb-0 ml:flex-col">
          <div className="flex-1 flex gap-8  items-center">
            <LocatorIcon className="w-20 h-auto ml:hidden" />
            <div className="body-1 ml:display-4">
              {window.TMW.Strings.storeLocator.findInStore}
            </div>
          </div>
          {currentVariant?.id && (!!userCurrentLocation || userSavedStore) && (
            <button
              className={clsx('body-3-underline hover:no-underline ml:mt-8', {
                'ml:hidden': !!userCurrentLocation || userSavedStore,
              })}
              onClick={() =>
                document.dispatchEvent(new CustomEvent('openStoreLocator'))
              }
            >
              {window.TMW.Strings.storeLocator.viewMore}
            </button>
          )}
        </div>
        {!currentVariant?.id && (
          <div className="body-3 ml:mt-8 ml:text-center">
            {window.TMW.Strings.product.checkAvailability}
          </div>
        )}
        {!!currentVariant?.id && (
          <>
            {!userCurrentLocation && !userSavedStore && (
              <button
                className="body-3-underline hover:no-underline ml:mt-8"
                onClick={() =>
                  document.dispatchEvent(new CustomEvent('openStoreLocator'))
                }
              >
                {window.TMW.Strings.storeLocator.viewMore}
              </button>
            )}
            {(!!userCurrentLocation || userSavedStore) && (
              <div className="mt-16 ml:mt-0">
                {stores.map((location) => (
                  <div
                    key={location.id}
                    className="p-24 border-b border-grey-400"
                  >
                    <div
                      className="flex gap-8 mb-8 cursor-pointer"
                      onClick={() =>
                        document.dispatchEvent(
                          new CustomEvent('openStoreLocator', {
                            detail: {
                              storeId: location.storeId,
                            },
                          }),
                        )
                      }
                    >
                      <div className="flex-1 body-2 ml:pl-20 ml:text-center">
                        {location.name}
                      </div>
                      <ChevronRightIcon className="w-12 h-auto" />
                    </div>
                    <div className="flex flex-wrap gap-y-8 gap-x-24 ml:justify-center">
                      {!!stockData && (
                        <div className="flex items-center gap-4 ">
                          <div
                            className={clsx(
                              'w-8 h-8 ml:w-5 ml:h-5 rounded-full',
                              {
                                'bg-misc-green':
                                  stockData[location.storeId] > 1,
                                'bg-misc-yellow':
                                  stockData[location.storeId] === 1,
                                'bg-misc-sale ml:bg-error':
                                  !stockData[location.storeId] ||
                                  stockData[location.storeId] < 1,
                              },
                            )}
                          ></div>
                          <div className="body-3">
                            {stockData[location.storeId] > 1
                              ? window.TMW.Strings.storeLocator.inStock
                              : stockData[location.storeId] === 1
                                ? window.TMW.Strings.storeLocator.lowStock
                                : window.TMW.Strings.storeLocator.outOfStock}
                          </div>
                        </div>
                      )}
                      {!!location && location.metersAway && (
                        <div className="body-3 text-subdued">
                          {window.TMW.Strings.storeLocator.distanceAway.replace(
                            '{{amount}}',
                            location.kmAway,
                          )}
                        </div>
                      )}
                      <div
                        className="body-3 text-subdued"
                        dangerouslySetInnerHTML={{
                          __html: getStoreStatus(location).summary,
                        }}
                      ></div>
                    </div>
                    {location?.storeId === userSavedStoreId && (
                      <div className="mt-8 flex gap-8 items-center ml:justify-center">
                        <div className="flex gap-4 p-4 bg-accent-2 ml:bg-grey-300 body-3 items-center">
                          <BookmarkIcon />
                          {window.TMW.Strings.storeLocator.myStore}
                        </div>
                        <button
                          className="body-3-underline hover:no-underline text-subdued"
                          onClick={() => setUserSavedStoreId(null)}
                        >
                          {window.TMW.Strings.storeLocator.removeMyStore}
                        </button>
                      </div>
                    )}
                    {!userSavedStoreId && (
                      <div className="mt-8 flex gap-8 items-center ml:justify-center">
                        <button
                          onClick={() => setUserSavedStoreId(location.storeId)}
                          className="body-3-underline hover:no-underline flex gap-8 lg:gap-4 ml:text-subdued"
                        >
                          <MapPinIcon className="w-16 h-auto ml:hidden" />
                          {window.TMW.Strings.storeLocator.setMyStore}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
                <div className="py-24 lc:hidden">
                  <button
                    className="body-3-underline hover:no-underline"
                    onClick={() =>
                      document.dispatchEvent(
                        new CustomEvent('openStoreLocator'),
                      )
                    }
                  >
                    {window.TMW.Strings.storeLocator.viewMore}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ProductStore;
